// src/components/AdminSettings.js
import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
  Alert,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import NotificationSettings from './NotificationSettings';

function AdminSettings() {
  const { user } = useAuth();
  const [message, setMessage] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Company Information (matches your invoice details)
  const [companyInfo, setCompanyInfo] = useState({
    name: 'HumberTrack Ltd',
    address: '34 Plantation Drive, North Ferriby, HU143BE',
    phone: '07897 201067',
    email: 'installs@humbertrack.co.uk',
    website: 'www.humbertrack.co.uk',
  });

  const [tempCompanyInfo, setTempCompanyInfo] = useState({ ...companyInfo });

  // Bank Details (matches your invoice details)
  const [bankDetails, setBankDetails] = useState({
    accountName: 'HumberTrack',
    bankName: 'STARLING BANK',
    sortCode: '60-83-71',
    accountNumber: '3929 5852',
  });

  const [tempBankDetails, setTempBankDetails] = useState({ ...bankDetails });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    autoBackup: true,
    requirePhotoUpload: true,
    defaultVATRate: 20,
    defaultDueDays: 30,
  });

  // Load settings from Firestore on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);

      // Load company info
      const companyDoc = await getDoc(doc(db, 'settings', 'company'));
      if (companyDoc.exists()) {
        const data = companyDoc.data();
        setCompanyInfo(data);
        setTempCompanyInfo(data);
      }

      // Load bank details
      const bankDoc = await getDoc(doc(db, 'settings', 'bank'));
      if (bankDoc.exists()) {
        const data = bankDoc.data();
        setBankDetails(data);
        setTempBankDetails(data);
      }

      // Load system settings
      const systemDoc = await getDoc(doc(db, 'settings', 'system'));
      if (systemDoc.exists()) {
        setSystemSettings(systemDoc.data());
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setIsLoading(false);
      // If settings don't exist, initialize them
      await initializeSettings();
    }
  };

  const initializeSettings = async () => {
    try {
      // Save default company info
      await setDoc(doc(db, 'settings', 'company'), companyInfo);
      
      // Save default bank details
      await setDoc(doc(db, 'settings', 'bank'), bankDetails);
      
      // Save default system settings
      await setDoc(doc(db, 'settings', 'system'), systemSettings);
      
      console.log('Settings initialized successfully');
    } catch (error) {
      console.error('Error initializing settings:', error);
    }
  };

  const handleSaveCompanyInfo = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'company'), tempCompanyInfo);
      setCompanyInfo(tempCompanyInfo);
      setEditingCompany(false);
      setMessage({ type: 'success', text: 'Company information saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving company info:', error);
      setMessage({ type: 'error', text: 'Failed to save company information' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelCompanyEdit = () => {
    setTempCompanyInfo({ ...companyInfo });
    setEditingCompany(false);
  };

  const handleSaveBankDetails = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'bank'), tempBankDetails);
      setBankDetails(tempBankDetails);
      setEditingBank(false);
      setMessage({ type: 'success', text: 'Bank details saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving bank details:', error);
      setMessage({ type: 'error', text: 'Failed to save bank details' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelBankEdit = () => {
    setTempBankDetails({ ...bankDetails });
    setEditingBank(false);
  };

  const handleSystemSettingChange = async (setting) => {
    const newSettings = {
      ...systemSettings,
      [setting]: !systemSettings[setting],
    };

    try {
      await setDoc(doc(db, 'settings', 'system'), { [setting]: newSettings[setting] }, { merge: true });
      setSystemSettings(newSettings);
      setMessage({ type: 'success', text: 'Setting updated and saved!' });
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      console.error('Error saving setting:', error);
      setMessage({ type: 'error', text: 'Failed to save setting' });
    }
  };

  const handleNumberSettingChange = async (setting, value) => {
    const numValue = parseInt(value) || 0;
    const newSettings = {
      ...systemSettings,
      [setting]: numValue,
    };

    try {
      await setDoc(doc(db, 'settings', 'system'), { [setting]: numValue }, { merge: true });
      setSystemSettings(newSettings);
      setMessage({ type: 'success', text: 'Setting saved!' });
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      console.error('Error saving number setting:', error);
      setMessage({ type: 'error', text: 'Failed to save setting' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: "linear-gradient(135deg, #60a5fa, #a78bfa)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 1,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}>
          Configure your business and system preferences
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      <Collapse in={!!message}>
        <Alert 
          severity={message?.type || 'info'} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message?.text}
        </Alert>
      </Collapse>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Push Notifications Settings - NEW */}
        <Grid item xs={12}>
          <NotificationSettings user={user} />
        </Grid>

        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(15,23,42,0.95))",
              border: "1px solid rgba(59,130,246,0.25)",
              mb: { xs: 2, md: 0 },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Company Information
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used on invoices and documents
                    </Typography>
                  </Box>
                </Box>
                {!editingCompany ? (
                  <IconButton 
                    onClick={() => setEditingCompany(true)}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon />
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      onClick={handleSaveCompanyInfo}
                      sx={{ color: 'success.main' }}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <CheckIcon />}
                    </IconButton>
                    <IconButton 
                      onClick={handleCancelCompanyEdit}
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                )}
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    Company Name
                  </Typography>
                  {editingCompany ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempCompanyInfo.name}
                      onChange={(e) => setTempCompanyInfo({ ...tempCompanyInfo, name: e.target.value })}
                      placeholder="Your Company Name"
                      sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>{companyInfo.name}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <LocationIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Address
                  </Typography>
                  {editingCompany ? (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={tempCompanyInfo.address}
                      onChange={(e) => setTempCompanyInfo({ ...tempCompanyInfo, address: e.target.value })}
                      placeholder="Full Address"
                      sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>{companyInfo.address}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Phone
                  </Typography>
                  {editingCompany ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempCompanyInfo.phone}
                      onChange={(e) => setTempCompanyInfo({ ...tempCompanyInfo, phone: e.target.value })}
                      placeholder="Phone Number"
                      sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>{companyInfo.phone}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Email
                  </Typography>
                  {editingCompany ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempCompanyInfo.email}
                      onChange={(e) => setTempCompanyInfo({ ...tempCompanyInfo, email: e.target.value })}
                      placeholder="Email Address"
                      sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>{companyInfo.email}</Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Details */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              background: "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(15,23,42,0.95))",
              border: "1px solid rgba(16,185,129,0.25)",
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                    <BankIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Bank Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For invoice payment information
                    </Typography>
                  </Box>
                </Box>
                {!editingBank ? (
                  <IconButton 
                    onClick={() => setEditingBank(true)}
                    sx={{ color: 'success.main' }}
                  >
                    <EditIcon />
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <IconButton 
                      onClick={handleSaveBankDetails}
                      sx={{ color: 'success.main' }}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <CheckIcon />}
                    </IconButton>
                    <IconButton 
                      onClick={handleCancelBankEdit}
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                )}
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Account Name
                  </Typography>
                  {editingBank ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempBankDetails.accountName}
                      onChange={(e) => setTempBankDetails({ ...tempBankDetails, accountName: e.target.value })}
                      placeholder="Account Name"
                    />
                  ) : (
                    <Typography variant="body1">{bankDetails.accountName}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Bank Name
                  </Typography>
                  {editingBank ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempBankDetails.bankName}
                      onChange={(e) => setTempBankDetails({ ...tempBankDetails, bankName: e.target.value })}
                      placeholder="Bank Name"
                    />
                  ) : (
                    <Typography variant="body1">{bankDetails.bankName}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Sort Code
                  </Typography>
                  {editingBank ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempBankDetails.sortCode}
                      onChange={(e) => setTempBankDetails({ ...tempBankDetails, sortCode: e.target.value })}
                      placeholder="00-00-00"
                    />
                  ) : (
                    <Typography variant="body1">{bankDetails.sortCode}</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Account Number
                  </Typography>
                  {editingBank ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={tempBankDetails.accountNumber}
                      onChange={(e) => setTempBankDetails({ ...tempBankDetails, accountNumber: e.target.value })}
                      placeholder="12345678"
                    />
                  ) : (
                    <Typography variant="body1">{bankDetails.accountNumber}</Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* System Preferences */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.1), rgba(15,23,42,0.95))",
              border: "1px solid rgba(139,92,246,0.25)",
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                  <NotificationsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    System Preferences
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Configure default settings and behaviors (auto-saved)
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Email & SMS
                    </Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={systemSettings.emailNotifications}
                            onChange={() => handleSystemSettingChange('emailNotifications')}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Email Notifications</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Receive email alerts for new jobs
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={systemSettings.smsNotifications}
                            onChange={() => handleSystemSettingChange('smsNotifications')}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">SMS Notifications</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Receive text messages for urgent updates
                            </Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Job Settings
                    </Typography>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={systemSettings.autoBackup}
                            onChange={() => handleSystemSettingChange('autoBackup')}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Auto Backup</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Automatically backup data daily
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={systemSettings.requirePhotoUpload}
                            onChange={() => handleSystemSettingChange('requirePhotoUpload')}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Require Photo Upload</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Engineers must upload photos to complete jobs
                            </Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      <ReceiptIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Invoice Defaults
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Default VAT Rate (%)"
                        type="number"
                        value={systemSettings.defaultVATRate}
                        onChange={(e) => handleNumberSettingChange('defaultVATRate', e.target.value)}
                        onBlur={() => {}}
                        helperText="Applied to new invoices by default"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Default Due Days"
                        type="number"
                        value={systemSettings.defaultDueDays}
                        onChange={(e) => handleNumberSettingChange('defaultDueDays', e.target.value)}
                        onBlur={() => {}}
                        helperText="Payment deadline in days"
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      <SecurityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Account & Security
                    </Typography>
                    <Stack spacing={2}>
                      <Button variant="outlined" size="small" fullWidth>
                        Change Password
                      </Button>
                      <Button variant="outlined" size="small" fullWidth>
                        Two-Factor Authentication
                      </Button>
                      <Button variant="outlined" color="error" size="small" fullWidth>
                        Export All Data
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminSettings;